/**
 * Polls and forwarding in the store (protocol polls.ts, 2026-10-29): the
 * vote events applied to a message, a poll's own counts kept through a
 * MESSAGE_UPDATE (the server sends one copy for everyone, without your own
 * votes), and the actions: vote (shown at once, put right if refused), end
 * now, post a poll, forward.
 */
import {
  MessageReferenceType,
  type Message,
  type PollCreateRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { api } from '../lib/api';
import { updateData, useData } from './data';
import { applyPollVote, keepMyVotes, pollCounts } from './pollMath';
import { upsertMessage } from './reducer';

export { applyPollVote, keepMyVotes, pollCounts, pollOver } from './pollMath';

function patch(m: Message, fn: (m: Message) => Message): void {
  updateData((s) => {
    const cur = s.messages[m.channel_id]?.byId[m.id];
    return cur ? upsertMessage(s, fn(cur)) : s;
  });
}

/** A message the API returned, into a channel whose latest history is on hand (as MESSAGE_CREATE). */
function live(channelId: Snowflake, m: Message): void {
  updateData((s) => {
    const cm = s.messages[channelId];
    return cm?.loaded && !cm.hasNewer ? upsertMessage(s, m) : s;
  });
}

/** Your whole vote (`[]` takes it back): shown at once, put back as it was if refused. */
export async function votePoll(m: Message, answerIds: number[]): Promise<void> {
  const before = useData.getState().messages[m.channel_id]?.byId[m.id] ?? m;
  const had = before.poll
    ? pollCounts(before.poll)
        .filter((c) => c.me_voted)
        .map((c) => c.id)
    : [];
  patch(m, (cur) => {
    let next = cur;
    for (const a of had)
      if (!answerIds.includes(a)) next = applyPollVote(next, { answer_id: a }, true, false);
    for (const a of answerIds)
      if (!had.includes(a)) next = applyPollVote(next, { answer_id: a }, true, true);
    return next;
  });
  try {
    await api.channels.pollVote(m.channel_id, m.id, answerIds);
  } catch (e) {
    patch(m, (cur) => ({ ...cur, poll: before.poll }));
    throw e;
  }
}

/** Ends a poll now (its author, or Manage Messages). */
export async function endPoll(m: Message): Promise<void> {
  const out = await api.channels.pollExpire(m.channel_id, m.id);
  patch(m, (cur) => ({ ...cur, ...keepMyVotes(cur, out) }));
}

/** Posts a poll in a channel. */
export async function sendPoll(channelId: Snowflake, poll: PollCreateRequest): Promise<void> {
  const m = await api.channels.send(channelId, {
    poll,
    nonce: `${Date.now()}${Math.floor(Math.random() * 1e6)}`.slice(0, 25),
  });
  live(channelId, m);
}

/**
 * Forwards `m` to each target in turn, then the comment as its own message
 * there (Discord's way). Returns how many went, and the first refusal.
 */
export async function forwardMessage(
  m: Message,
  targets: Snowflake[],
  comment: string,
): Promise<{ ok: number; error: unknown }> {
  let ok = 0;
  let error: unknown = null;
  for (const target of targets) {
    try {
      const out = await api.channels.send(target, {
        message_reference: {
          type: MessageReferenceType.FORWARD,
          message_id: m.id,
          channel_id: m.channel_id,
          ...(m.guild_id ? { guild_id: m.guild_id } : {}),
        },
      });
      live(target, out);
      ok += 1;
      if (comment.trim())
        live(target, await api.channels.send(target, { content: comment.trim() }));
    } catch (e) {
      error ??= e;
    }
  }
  return { ok, error };
}
